import { h } from '../lib/dom.js';
import { listScenarios } from '../lib/storage.js';
import { money, pct } from '../lib/format.js';

export function HomePage() {
  const recent = listScenarios()
    .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
    .slice(0, 4);

  return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' },
    masthead(),
    actions(),
    recent.length ? ledger(recent) : tutorial(),
  );
}

function masthead() {
  return h('section', { class: 'pt-12 md:pt-20 pb-10 md:pb-16 grid md:grid-cols-12 gap-8 items-end border-b border-[color:var(--color-rule)]' },
    h('div', { class: 'md:col-span-8' },
      h('div', { class: 'folio mb-5' }, 'Vol. 01 · Feasibility & site selection'),
      h('h1', { class: 'display display-lg' },
        'A calmer way',
        h('br'),
        'to price a ',
        h('span', { class: 'italic' }, 'subdivision'),
        '.',
      ),
    ),
    h('div', { class: 'md:col-span-4 md:pl-6 md:border-l border-[color:var(--color-rule)]' },
      h('p', { class: 'text-[color:var(--color-ink-soft)]' },
        'Built for the way we already work — the Bluehills sheet, the builder\u2019s estimate, the gut feel about a corner block — and turned into something you can run in under a minute.',
      ),
    ),
  );
}

function actions() {
  const items = [
    {
      kind: 'primary', href: '#/feaso', n: '01',
      title: 'Price a deal', blurb: 'Purchase, build, GST margin scheme, profit on cost. Live as you type.',
    },
    {
      href: '#/scenarios', n: '02',
      title: 'Open the ledger', blurb: 'Every feasibility you\u2019ve run, sorted by what\u2019s actually worth doing.',
    },
    {
      href: '#/finder', n: '03',
      title: 'Find the next site', blurb: 'Corner blocks in the right zones, scored by auto-feasibility.',
    },
    {
      href: '#/library', n: '04',
      title: 'Set your defaults', blurb: 'Stamp duty, loan rates, $/m², subdivision fees — tuned to your projects.',
    },
  ];

  return h('section', { class: 'py-10 md:py-14 grid grid-cols-1 md:grid-cols-2 gap-0' },
    ...items.map((it, i) => h('a', {
      href: it.href,
      class: [
        'group relative flex flex-col justify-between gap-10 p-6 md:p-10 min-h-[14rem]',
        'border-[color:var(--color-rule)]',
        // internal rule grid — avoids cards
        i % 2 === 0 ? 'md:border-r' : '',
        i < items.length - 2 ? 'border-b' : 'md:border-b-0 border-b',
        it.kind === 'primary' ? 'bg-[color:var(--color-olive-50)]' : '',
        'hover:bg-[color:var(--color-paper-deep)] transition-colors',
      ].join(' '),
    },
      h('div', { class: 'flex items-baseline gap-4' },
        h('span', { class: 'folio num' }, it.n),
        h('span', { class: 'h-px flex-1 bg-[color:var(--color-rule)]' }),
      ),
      h('div', { class: 'space-y-2' },
        h('h2', { class: 'display display-md' }, it.title),
        h('p', { class: 'text-[color:var(--color-ink-soft)] max-w-[38ch]' }, it.blurb),
      ),
      h('div', { class: 'folio text-[color:var(--color-olive-700)] opacity-0 group-hover:opacity-100 transition-opacity' },
        'Open →',
      ),
    )),
  );
}

function ledger(recent) {
  return h('section', { class: 'py-10 md:py-14 border-t border-[color:var(--color-rule)]' },
    h('div', { class: 'flex items-baseline justify-between mb-6' },
      h('h2', { class: 'display display-md' }, 'Recent from the ledger'),
      h('a', { href: '#/scenarios', class: 'folio hover:text-[color:var(--color-ink)]' }, 'See all →'),
    ),
    h('ol', { class: 'divide-y divide-[color:var(--color-rule)] border-y border-[color:var(--color-rule)]' },
      ...recent.map((s, i) => h('li', null, ledgerRow(s, i + 1))),
    ),
  );
}

function ledgerRow(s, n) {
  const poc = s.outputs?.profit_on_cost_pct ?? 0;
  const profit = s.outputs?.forecast_profit ?? 0;
  const signal =
    poc >= 0.15 ? 'text-[color:var(--color-positive)]'
    : poc >= 0.10 ? 'text-[color:var(--color-warning)]'
    : 'text-[color:var(--color-negative)]';

  return h('a', {
    href: `#/feaso/${s.id}`,
    class: 'grid grid-cols-12 gap-4 items-baseline py-5 px-1 hover:bg-[color:var(--color-paper-deep)] transition-colors',
  },
    h('div', { class: 'col-span-1 folio num' }, String(n).padStart(2, '0')),
    h('div', { class: 'col-span-5' },
      h('div', { class: 'display display-sm' }, s.name || s.inputs?.address || 'Untitled'),
      s.inputs?.address && s.inputs.address !== s.name
        ? h('div', { class: 'text-[color:var(--color-ink-dim)] text-sm' }, s.inputs.address) : null,
    ),
    h('div', { class: 'col-span-2 num text-[color:var(--color-ink-soft)]' },
      `${s.inputs?.dwellings ?? '—'} × ${money(s.inputs?.sale_price_per_dwelling ?? 0)}`,
    ),
    h('div', { class: `col-span-2 num display display-sm ${signal}` }, money(profit)),
    h('div', { class: `col-span-2 text-right num ${signal}` }, pct(poc)),
  );
}

function tutorial() {
  return h('section', { class: 'py-14 md:py-20 border-t border-[color:var(--color-rule)] grid md:grid-cols-12 gap-10' },
    h('div', { class: 'md:col-span-8 space-y-5' },
      h('div', { class: 'folio' }, 'Before your first feasibility'),
      h('div', { class: 'display display-md max-w-[22ch]' },
        'Price a deal, save the scenario, come back when the sellers move.',
      ),
      h('ol', { class: 'mt-6 space-y-4 text-[color:var(--color-ink-soft)] max-w-[56ch]' },
        h('li', null,
          h('span', { class: 'folio num mr-3' }, '01'),
          'Start on Feasibility. The defaults are seeded from 65 Bluehills and the Burbank Stirling 134 estimate, so you can hit a real number with three fields filled in.',
        ),
        h('li', null,
          h('span', { class: 'folio num mr-3' }, '02'),
          'Override anything. Build rate, subdivision fee, loan rates — the ledger panel shows the math as you change it.',
        ),
        h('li', null,
          h('span', { class: 'folio num mr-3' }, '03'),
          'Save to the ledger. When the Finder comes online, scored listings will feed straight into this same flow.',
        ),
      ),
      h('div', { class: 'pt-6' },
        h('a', { href: '#/feaso', class: 'btn btn-ink' }, 'Begin →'),
      ),
    ),
    h('aside', { class: 'md:col-span-4 md:pl-8 md:border-l border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-dim)]' },
      h('div', { class: 'folio mb-3 text-[color:var(--color-ink-soft)]' }, 'Provenance'),
      h('p', { class: 'mb-3' },
        'The calculation faithfully reproduces the ',
        h('em', { class: 'italic' }, '1 into 3 · 65 Bluehills O\u2019Halloran Hill'),
        ' feasibility to the dollar.',
      ),
      h('p', null,
        'Builder cost inputs are seeded from ',
        h('em', { class: 'italic' }, 'Sterling 134 · Sheffield'),
        ' (Burbank, Apr 2025) — $2,079/m\u00b2 in the estimate, rounded to $2,300/m\u00b2 for forward cover.',
      ),
    ),
  );
}
